/**
 * module-list-columns.tsx — kolumny listy kartoteki WŁASNEJ (modułu z kreatora): kolumny
 * systemowe (nazwa, numer, kategoria) + kolumny pól modułu z konfiguracją FILTRA wiersza
 * filtrów per typ pola.
 *
 * Jedno źródło prawdy dla wszystkich ekranów pokazujących listę kartoteki własnej (strona
 * listy `ModuleRecordsPage`, sekcja kokpitu decyzji): ten sam zestaw kolumn i te same
 * kontrolki filtra. Zasady wiersza filtrów (decyzja właścicielki): w KAŻDEJ kolumnie z danymi
 * jest działająca kontrolka, bez martwych pól; kolumna celowo bez filtra ma PUSTĄ komórkę
 * (nigdy generyczne „Szukaj…"). Wiersz nie ma menu operatorów: tekst = „zawiera”,
 * liczba/data = „równa się” (inne operatory tylko w popupie lejka nagłówka).
 *
 * Filtr per typ pola (`moduleFieldFilter`):
 *  - adres → tekst „zawiera” po całym adresie (serwer przeszukuje ulicę, kod, miasto, kraj);
 *  - zakres dat → data „równa się” po dacie POCZĄTKU zakresu;
 *  - lokalizacja → BRAK filtra (pusta komórka);
 *  - lista wyboru / radio / status / lista wielokrotna / tagi / grupa checkboxów → lista
 *    z NAZWAMI opcji z definicji pola (wartość = to, co rekord trzyma w kolumnie; dla pól
 *    wielowartościowych serwer traktuje wybór jako „zawiera wybraną opcję”). Lista wyboru
 *    (`select`) ze słownikiem (`dictionaryCode`) — lista z NAZWAMI pozycji słownika (wartość =
 *    kod pozycji, opcje wczytuje `useModuleListFilterOptions`) także wtedy, gdy ma opcje
 *    w definicji: formularz rekordu pokazuje i zapisuje wtedy pozycje słownika, więc filtr musi
 *    pasować do zapisanych kodów. Pozostałe typy wyboru ze słownikiem — pozycje słownika, ale
 *    tylko gdy pole nie ma opcji w definicji (te mają pierwszeństwo). Dopóki słownik się
 *    wczytuje, komórka filtra jest PUSTA (tekst pokazałby się na chwilę i zniknął pod listą);
 *    gdy pozycji słownika nie ma (słownik z parametrami, awaria wczytania, słownik pusty) —
 *    filtr tekstowy „zawiera” po zapisanej wartości (nie opcje z definicji: formularz pola ze
 *    słownikiem ich nie zapisuje);
 *  - użytkownik → lista użytkowników z imionami (jak „Opiekun” na listach systemowych) plus
 *    opcja „(Puste)” (rekordy bez wartości); bez opcji (brak prawa podglądu użytkowników) —
 *    pusta komórka;
 *  - relacja → lista z NAZWAMI rekordów (opcje z endpointu podpowiedzi, patrz
 *    `relationFilterOptionsFromDistinct`) plus opcja „(Puste)”; bez opcji — pusta komórka.
 *    Komórki bez zmian. Oba pola to `record-select` (wyszukiwalna lista w wierszu filtrów,
 *    bez lejka w nagłówku), więc „(Puste)” musi być jedną z opcji tej listy
 *    (`MODULE_FILTER_EMPTY_VALUE`, zamieniana na `null` przed wysyłką po TYPIE POLA — patrz
 *    `emptyFilterValuesToNull` i `buildModuleListHeaderFilters`);
 *  - data i godzina → data „równa się” (serwer bierze cały dzień);
 *  - reszta jak dotąd: tekst „zawiera”, liczby/daty „równa się”, checkbox/przełącznik Tak/Nie.
 *
 * Opcje dołożone WYŁĄCZNIE dla wiersza filtrów mają `kanbanFallback: false` — nie zmieniają
 * domyślnego pola grupującego Kanbana tabeli. Kanban strony listy używa OSOBNYCH kolumn
 * (`buildModuleKanbanColumns`) z dotychczasowymi regułami (opcje pola wyboru = tory), więc jego
 * tory się nie zmieniają.
 *
 * Widoczność, kolejność i szerokość kolumn nakłada sama tabela z układu listy (`tableId`, patrz
 * `moduleListTableId`); kolumny z konfiguracji kolumn strony (localStorage) wybiera wołający
 * i podaje jako `fields`.
 *
 * WYMÓG dla każdego ekranu, który wysyła filtry kolumn do serwera: tablicę `headerFilters` żądania
 * buduje `buildModuleListHeaderFilters` (zamienia opcję „(Puste)” na `null` po typie pola z PEŁNEJ
 * listy aktywnych pól, a nie po kolumnach widocznych — filtr kolumny ukrytej po wyborze „(Puste)”
 * nadal idzie do serwera), nie surowy stan filtrów tabeli.
 */

import type {
  ColumnDefinition,
  ColumnFilters,
  ColumnHeaderFilter,
  ColumnHeaderFilterConfig,
  ColumnHeaderFilters,
} from '@/components/configurable-table';
import { FieldRegistry, type ModuleField } from '@/components/fields/FieldRegistry';
import type { ModuleRecord } from '@/components/modules/record-types';
import {
  buildListHeaderFilters,
  type ListFilterOption,
  type ListFilterSources,
} from '@/lib/ext-list-columns';

/** Tłumaczenie klucza (zgodne z `t` z `useTranslation`). */
export type ModuleListTranslateFn = (key: string) => string;

/**
 * Wiersz listy kartoteki własnej w minimalnym kształcie, który czyta builder kolumn: `id` i
 * wartości pól pod slugami (kolumny systemowe — `record_name`, `record_number`, `category_id`,
 * `is_archived` — czytane defensywnie, bo typ nie gwarantuje ich obecności). Spełniają go
 * `ModuleRecord` (strona listy) i wiersz sekcji kokpitu decyzji (`DecisionSectionRow`) — dzięki
 * temu builder jest ogólny po typie wiersza i sekcja nie potrzebuje rzutu typów.
 */
export interface ModuleListRow {
  id: string;
  [slug: string]: unknown;
}

/** Tekst z wartości wiersza albo `null`, gdy wartość nie jest tekstem (np. brak, `null`). */
function cellText(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

/**
 * Wartość opcji „(Puste)” w filtrze pól „użytkownik” i „relacja” (rekordy bez wartości w polu).
 * To znacznik po stronie panelu: stan filtra trzyma go jak zwykłą wartość (lista wyboru pokazuje
 * opcję jako zaznaczoną), a PRZED wysyłką do serwera `emptyFilterValuesToNull` zamienia go na
 * `null` w `selectedValues` — serwer czyta `null` jako „pole puste” (IS NULL).
 */
export const MODULE_FILTER_EMPTY_VALUE = '__empty__';

/** Kategoria rekordów modułu — to, czego potrzebuje lista (kolumna „Kategoria”, numeracja). */
export interface ModuleListCategory {
  id: string;
  name: string;
  color: string;
  numberingConfig?: { enabled: boolean } | null;
}

/**
 * Opcje filtra, które trzeba wczytać (nie wynikają z definicji pola): użytkownicy tenanta,
 * rekordy celów relacji i pozycje słowników pól wyboru. Brak opcji użytkowników / relacji =
 * kolumna bez kontrolki filtra (pusta komórka); brak pozycji słownika pola ze słownikiem = filtr
 * tekstowy, chyba że słownik dopiero się wczytuje (`dictionaryPendingSlugs`) — wtedy pusta
 * komórka.
 */
export interface ModuleListFilterOptions {
  /** Użytkownicy dla pól „użytkownik” (wartość = id, etykieta = nazwa; nigdy e-mail). */
  userOptions: ReadonlyArray<ListFilterOption>;
  /** Rekordy dla pól „relacja” wg sluga pola (wartość = id rekordu, etykieta = jego nazwa). */
  relationOptions: Readonly<Record<string, ReadonlyArray<ListFilterOption>>>;
  /**
   * Pozycje słowników dla pól wyboru wg sluga pola: pól `select` ze słownikiem (także z opcjami
   * w definicji — formularz pokazuje wtedy słownik) oraz pól innych typów wyboru ze słownikiem
   * BEZ opcji w definicji. Wartość = kod pozycji (to, co rekord trzyma w kolumnie); etykieta =
   * nazwa pozycji wg trybu wyświetlania słownika / pola.
   */
  dictionaryOptions: Readonly<Record<string, ReadonlyArray<ListFilterOption>>>;
  /**
   * Slugi pól wyboru, których słownik jeszcze się wczytuje (ani pozycji, ani błędu). Ich komórka
   * filtra jest pusta, a nie tekstowa: pole tekstowe znikałoby po chwili pod listą wyboru,
   * razem z już wpisanym filtrem. Dotyczy też pola `select` z opcjami w definicji — po
   * wczytaniu pozycje słownika zastąpiłyby jego opcje.
   */
  dictionaryPendingSlugs: ReadonlySet<string>;
}

/** Brak wczytanych opcji — wartość domyślna dla wołających, którzy ich nie wczytują. */
export const EMPTY_MODULE_LIST_FILTER_OPTIONS: ModuleListFilterOptions = {
  userOptions: [],
  relationOptions: {},
  dictionaryOptions: {},
  dictionaryPendingSlugs: new Set<string>(),
};

/**
 * Pola liczbowe — filtr wiersza „równe / większe / …" (`category: 'number'`). Wszystkie typy, które
 * kreator zapisuje w kolumnie liczbowej tabeli kartoteki (`mapFieldTypeToPgType` w `ddl.ts` serwera):
 * `number`/`autonumber` → INTEGER, `decimal` → NUMERIC, `currency` → NUMERIC(15,2), `percent`/`slider` →
 * NUMERIC(10,4), `rating` → SMALLINT (`integer` — dawna nazwa liczby całkowitej). Typ pominięty tutaj
 * dostałby filtr tekstowy „zawiera" na kolumnie liczbowej.
 */
const NUMERIC_FIELD_TYPES = new Set([
  'number',
  'currency',
  'percent',
  'rating',
  'integer',
  'decimal',
  'slider',
  'autonumber',
]);
/**
 * Pola liczbowe w kolumnach Kanbana — bez `slider` / `autonumber` (Kanban celowo zostaje przy
 * kategorii tekstowej dla tych typów; liczbowy filtr tylko w wierszu filtrów listy i sekcji).
 * Zbiór wyprowadzony z `NUMERIC_FIELD_TYPES`, żeby nie kopiować pozycji.
 */
const KANBAN_NUMERIC_FIELD_TYPES: ReadonlySet<string> = new Set(
  Array.from(NUMERIC_FIELD_TYPES).filter((type) => type !== 'slider' && type !== 'autonumber'),
);
const DATE_FIELD_TYPES = new Set(['date', 'datetime']);
/**
 * Pola, których filtr wiersza to lista użytkowników / rekordów z opcją „(Puste)” (jedyne pola,
 * w których jej znacznik `MODULE_FILTER_EMPTY_VALUE` jest czymś innym niż zwykłą wartością).
 */
const EMPTY_OPTION_FIELD_TYPES: ReadonlySet<string> = new Set(['user', 'relation']);
const BOOLEAN_FIELD_TYPES = new Set(['checkbox', 'boolean', 'toggle']);
/** Pola z listą opcji w definicji pola — lista wyboru w wierszu filtrów. */
const OPTION_FIELD_TYPES = new Set([
  'select',
  'radio',
  'status',
  'multiselect',
  'tags',
  'checkboxgroup',
]);

/**
 * Opcje filtra z definicji pola (`options`): wartość = to, co rekord trzyma w kolumnie. Tagi
 * trzymają w rekordzie TEKST tagu (podpowiedzi formularza to etykiety opcji), nie kod opcji —
 * dla nich wartością jest etykieta. Opcje o powtórzonej wartości pomijamy; kolejność jak
 * w definicji pola; kolor opcji (też tagu) przechodzi do opcji filtra jak kolor pozycji słownika.
 */
export function fieldFilterOptions(field: ModuleField): ListFilterOption[] {
  const seen = new Set<string>();
  const result: ListFilterOption[] = [];
  for (const option of field.options ?? []) {
    const value = field.fieldType === 'tags' ? option.label : option.value;
    if (typeof value !== 'string' || value === '' || seen.has(value)) continue;
    seen.add(value);
    result.push({
      value,
      label: option.label?.trim() || value,
      ...(option.color ? { color: option.color } : {}),
    });
  }
  return result;
}

/**
 * Czy pole ma użyteczne opcje w definicji (`fieldFilterOptions` niepuste). Predykat wyboru
 * słownika do wczytania (`fieldDictionaryCode`) dla pól wyboru innych niż `select` (te mają
 * opcje z definicji przed słownikiem) — pole z samymi nieużytecznymi opcjami (puste wartości)
 * dostaje wtedy słownik, a nie pustą listę.
 */
export function fieldHasFilterOptions(field: ModuleField): boolean {
  return fieldFilterOptions(field).length > 0;
}

/** Konfiguracja filtra jednego pola modułu. */
export interface ModuleFieldFilterSpec {
  /** `undefined` = kolumna bez konfiguracji filtra (tylko lokalizacja). */
  headerFilter?: ColumnHeaderFilterConfig;
  /**
   * `false` = kolumna CELOWO bez kontrolki filtra: komórka wiersza filtrów jest pusta, nigdy
   * generyczne pole „Szukaj…” (`column.filterable: false`). `true` także dla kolumn z listą
   * `record-select` (użytkownik, relacja z opcjami): kontrolka typowana wygrywa z polem
   * „Szukaj…”, więc flaga niczego tam nie zmienia — `false` zostaje tylko tam, gdzie kontrolki
   * nie ma wcale.
   */
  filterable: boolean;
}

/**
 * Konfiguracja filtra pola modułu wg jego typu (patrz nagłówek pliku). Czysta funkcja — opcje
 * użytkowników/relacji/słowników przychodzą z zewnątrz, bo trzeba je wczytać.
 * `emptyOptionLabel` — etykieta opcji „(Puste)” list użytkowników i relacji (tekst tłumaczony,
 * więc podaje go wołający); bez niej lista nie ma tej opcji.
 */
export function moduleFieldFilter(
  field: ModuleField,
  filterOptions: ModuleListFilterOptions = EMPTY_MODULE_LIST_FILTER_OPTIONS,
  emptyOptionLabel?: string,
): ModuleFieldFilterSpec {
  const type = field.fieldType;
  const identity = { fieldSlug: field.slug, fieldType: type };

  if (type === 'location') return { filterable: false };

  // Pola złożone: nie mają kolumny o nazwie sluga, więc popup „Wartości” (endpoint podpowiedzi)
  // nie działa — tylko zakładka „Warunek”, z operatorami, które serwer obsługuje dla pola
  // złożonego.
  if (type === 'address') {
    return {
      filterable: true,
      headerFilter: { category: 'text', ...identity, conditionOnly: true, operators: ['contains'] },
    };
  }
  if (type === 'daterange') {
    return {
      filterable: true,
      headerFilter: { category: 'date', ...identity, conditionOnly: true, operators: ['eq'] },
    };
  }

  if (EMPTY_OPTION_FIELD_TYPES.has(type)) {
    const options =
      type === 'user' ? filterOptions.userOptions : filterOptions.relationOptions[field.slug];
    if (options && options.length > 0) {
      // „(Puste)” jako pierwsza opcja: `record-select` nie ma lejka w nagłówku, więc to jedyna
      // droga do filtrowania rekordów bez wartości w polu.
      const withEmpty =
        emptyOptionLabel === undefined
          ? options
          : [{ value: MODULE_FILTER_EMPTY_VALUE, label: emptyOptionLabel }, ...options];
      return {
        // Lista do kilkuset pozycji → wyszukiwalny wybór (jak „Opiekun”); `kanbanFallback:
        // false` — opcje dla wiersza filtrów nie mają tworzyć torów Kanbana.
        filterable: true,
        headerFilter: {
          category: 'record-select',
          ...identity,
          options: withEmpty.map((option) => ({ ...option })),
          kanbanFallback: false,
        },
      };
    }
    // Bez opcji: pusta komórka (nie pole tekstowe po uuid); konfiguracja z `fieldSlug` zostaje,
    // żeby szybkie filtry listy dalej mapowały kolumnę na pole.
    return { filterable: false, headerFilter: { category: 'record-select', ...identity } };
  }

  if (OPTION_FIELD_TYPES.has(type)) {
    // Źródło opcji rozstrzyga DEFINICJA POLA, jak formularz rekordu (i `fieldDictionaryCode` po
    // stronie haka): pole ze słownikiem w `select` bierze pozycje słownika także przy opcjach
    // w definicji (formularz pokazuje i zapisuje wtedy pozycje słownika); w pozostałych typach
    // wyboru słownik jest źródłem tylko przy braku opcji w definicji. Gdy źródłem jest słownik,
    // a pozycji nie ma (słownik z parametrami — lista zależy od rekordu; awaria wczytania; słownik
    // pusty), zostaje filtr tekstowy po zapisanej wartości — NIE opcje z definicji, których
    // formularz takiego pola nie zapisuje.
    const inline = fieldFilterOptions(field);
    const fromDictionary = filterOptions.dictionaryOptions[field.slug] ?? [];
    const hasDictionary = (field.dictionaryCode?.trim() ?? '') !== '';
    const dictionaryIsSource = hasDictionary && (type === 'select' || inline.length === 0);
    // Słownik w trakcie wczytywania: pusta komórka (jak przy użytkowniku/relacji bez opcji). Pole
    // tekstowe — ani opcje z definicji pola `select` — pokazałyby się na chwilę i zniknęły pod listą
    // słownika, razem z już wpisanym filtrem. `category: 'none'` + `fieldSlug`: bez kontrolki i bez
    // lejka w nagłówku, a szybkie filtry listy dalej mapują kolumnę na pole.
    if (
      dictionaryIsSource &&
      fromDictionary.length === 0 &&
      filterOptions.dictionaryPendingSlugs.has(field.slug)
    ) {
      return { filterable: false, headerFilter: { category: 'none', ...identity } };
    }
    const options = dictionaryIsSource ? fromDictionary : inline;
    if (options.length > 0) {
      return {
        filterable: true,
        // `kanbanFallback: false` — opcje dla wiersza filtrów nie tworzą torów Kanbana.
        headerFilter: {
          category: 'select',
          ...identity,
          options: [...options],
          kanbanFallback: false,
        },
      };
    }
    // Opcji brak (pole bez opcji w definicji i bez słownika, słownik z parametrami, słownik
    // niedostępny albo bez pozycji) — filtr po zapisanej wartości.
    return { filterable: true, headerFilter: { category: 'text', ...identity } };
  }

  if (NUMERIC_FIELD_TYPES.has(type)) {
    return { filterable: true, headerFilter: { category: 'number', ...identity } };
  }
  if (DATE_FIELD_TYPES.has(type)) {
    return { filterable: true, headerFilter: { category: 'date', ...identity } };
  }
  if (BOOLEAN_FIELD_TYPES.has(type)) {
    return { filterable: true, headerFilter: { category: 'boolean', ...identity } };
  }
  return { filterable: true, headerFilter: { category: 'text', ...identity } };
}

/** Czy którykolwiek typ rekordu ma włączoną numerację — wtedy lista ma kolumnę „Numer”. */
export function moduleHasNumbering(categories: ReadonlyArray<ModuleListCategory>): boolean {
  return categories.some((category) => category.numberingConfig?.enabled === true);
}

/**
 * Identyfikator tabeli (`tableId`) listy kartoteki własnej — klucz układu listy i podpowiedzi
 * filtra. Układ wymuszony z pozycji menu dopisuje sufiks `::layout-<id>` (blokuje przełączanie
 * układu).
 */
export function moduleListTableId(moduleId: string, forcedListLayoutId?: string | null): string {
  return forcedListLayoutId
    ? `module-${moduleId}::layout-${forcedListLayoutId}`
    : `module-${moduleId}`;
}

/**
 * Opcje filtra pola „relacja” z odpowiedzi endpointu podpowiedzi
 * (`GET /modules/:id/records/distinct?field=<slug>` → `values[]`): wartość = id rekordu,
 * etykieta = jego nazwa (serwer podaje ją dla pól relacji). Kubełek „puste” (`value: null`)
 * pomijamy — „(Puste)” dokłada osobno `moduleFieldFilter`; powtórzone id też. Alfabetycznie po
 * etykiecie, wg języka interfejsu (`locale`, np. `i18n.language`; pusty = domyślny przeglądarki).
 */
export function relationFilterOptionsFromDistinct(
  values: ReadonlyArray<{ value: string | null; label: string }>,
  locale: string,
): ListFilterOption[] {
  const seen = new Set<string>();
  const options: ListFilterOption[] = [];
  for (const entry of values) {
    if (entry.value == null || entry.value === '' || seen.has(entry.value)) continue;
    seen.add(entry.value);
    options.push({ value: entry.value, label: entry.label?.trim() || entry.value });
  }
  const collator = new Intl.Collator(locale === '' ? undefined : locale);
  return options.sort((a, b) => collator.compare(a.label, b.label));
}

/**
 * Filtry nagłówka w postaci wysyłanej do serwera: opcja „(Puste)” (`MODULE_FILTER_EMPTY_VALUE`) w
 * filtrze pola „użytkownik” albo „relacja” staje się `null` w `selectedValues` — serwer czyta je
 * jako „pole puste”. Rozstrzyga TYP POLA z listy `fields` (id kolumny pola = slug pola), a nie
 * kolumny widoczne w tabeli: filtr zostaje w stanie strony także po ukryciu kolumny i dalej idzie
 * do serwera, więc musi dostać `null`, a nie surowy znacznik. Filtry innych kolumn (w tym
 * systemowych: nazwa, numer, kategoria) zostają bez zmian — zwykła wartość o tym samym zapisie nie
 * jest znacznikiem. Bez „(Puste)” w żadnym filtrze zwraca TEN SAM obiekt (stabilna tożsamość dla
 * `useMemo`).
 *
 * `fields` = WSZYSTKIE aktywne pola kartoteki (strona listy: pola modułu; sekcja kokpitu: pola
 * katalogu z odpowiedzi sekcji), nie tylko te, które mają teraz kolumnę.
 */
export function emptyFilterValuesToNull(
  headerFilters: ColumnHeaderFilters,
  fields: ReadonlyArray<{ slug: string; fieldType: string }>,
): ColumnHeaderFilters {
  let converted: ColumnHeaderFilters | null = null;
  for (const [columnId, filter] of Object.entries(headerFilters)) {
    if (filter.mode !== 'values' || !filter.selectedValues?.includes(MODULE_FILTER_EMPTY_VALUE)) {
      continue;
    }
    const field = fields.find((candidate) => candidate.slug === columnId);
    if (field === undefined || !EMPTY_OPTION_FIELD_TYPES.has(field.fieldType)) continue;
    converted ??= { ...headerFilters };
    converted[columnId] = {
      ...filter,
      selectedValues: filter.selectedValues.map((value) =>
        value === MODULE_FILTER_EMPTY_VALUE ? null : value,
      ),
    };
  }
  return converted ?? headerFilters;
}

/** Argumenty `buildModuleListHeaderFilters`: źródła filtrów listy + pełna lista pól kartoteki. */
export interface ModuleListFilterSources extends Omit<ListFilterSources, 'columnFilters'> {
  /** WSZYSTKIE aktywne pola kartoteki (patrz `emptyFilterValuesToNull`). */
  fields: ReadonlyArray<{ slug: string; fieldType: string }>;
  /** Wiersz „Szukaj…” pod nagłówkami; lista kartoteki go nie kontroluje, sekcja kokpitu tak. */
  columnFilters?: ColumnFilters;
}

/**
 * JEDNA droga „filtry kolumn listy kartoteki własnej → tablica `headerFilters` żądania” dla
 * strony listy (`ModuleRecordsPage`) i sekcji kokpitu decyzji: opcja „(Puste)” użytkownika /
 * relacji idzie jako `null` (`emptyFilterValuesToNull`, po typie pola z pełnej listy pól), a
 * źródła filtrów łączy wspólna serializacja list (`buildListHeaderFilters`: popup nagłówka i
 * wiersz filtrów → pole filtra z `headerFilter.fieldSlug` kolumny, wiersz „Szukaj…”, szybki
 * filtr). Wołający dopisuje własne warunki (np. filtry stałe z pozycji menu) do wyniku.
 */
export function buildModuleListHeaderFilters(args: ModuleListFilterSources): ColumnHeaderFilter[] {
  const { fields, headerFilters, columnFilters = {}, ...rest } = args;
  return buildListHeaderFilters({
    ...rest,
    headerFilters: emptyFilterValuesToNull(headerFilters, fields),
    columnFilters,
  });
}

/** Argumenty `buildModuleListColumns`. */
export interface BuildModuleListColumnsArgs {
  t: ModuleListTranslateFn;
  /**
   * Pola, które mają dostać kolumny, w kolejności kolumn. Strona listy podaje widoczne wg
   * konfiguracji kolumn (localStorage); inny ekran może podać wszystkie aktywne pola —
   * widoczność i kolejność nałoży tabela z układu listy.
   */
  fields: ReadonlyArray<ModuleField>;
  /** Kategorie rekordów modułu; niepuste = kolumna „Kategoria” (filtr z listą kategorii). */
  categories: ReadonlyArray<ModuleListCategory>;
  /** Czy któryś typ rekordu ma numerację (`moduleHasNumbering`) — wtedy kolumna „Numer”. */
  hasNumbering: boolean;
  /** Slugi kolumn sortowalnych serwerowo (`meta.sortable` odpowiedzi listy). */
  sortableSlugs: ReadonlyArray<string>;
  /**
   * Wczytane opcje filtra użytkowników/relacji/słowników; domyślnie puste (kolumny użytkownika
   * i relacji bez kontrolki, pola wyboru ze słownikiem z filtrem tekstowym).
   */
  filterOptions?: ModuleListFilterOptions;
}

/**
 * Kolumny tabeli listy kartoteki własnej: nazwa, [numer], [kategoria], potem jedna kolumna na
 * pole (`id` kolumny = slug pola — spójnie z układem listy, Kanbanem i danymi wiersza
 * `row[slug]`). Komórki pól rysuje `FieldRegistry.renderCell`; filtr kolumny pola —
 * `moduleFieldFilter`. Ogólna po typie wiersza (`ModuleListRow`): strona listy woła ją z
 * `ModuleRecord`, sekcja kokpitu decyzji z wierszem sekcji.
 *
 * WYMÓG dla wołającego: filtry kolumn tych kolumn wysyłaj do serwera WYŁĄCZNIE przez
 * `buildModuleListHeaderFilters` — opcja „(Puste)” użytkownika / relacji jest znacznikiem panelu
 * (`MODULE_FILTER_EMPTY_VALUE`), który bez zamiany na `null` dałby serwerowi pustą listę.
 */
export function buildModuleListColumns<TRow extends ModuleListRow>(
  args: BuildModuleListColumnsArgs,
): ColumnDefinition<TRow>[] {
  const {
    t,
    fields,
    categories,
    hasNumbering,
    sortableSlugs,
    filterOptions = EMPTY_MODULE_LIST_FILTER_OPTIONS,
  } = args;
  const sortable = new Set(sortableSlugs);
  // „(Puste)” list użytkowników i relacji — ten sam tekst co w popupie filtra nagłówka.
  const emptyOptionLabel = t('common.lists.emptyValue');

  const cols: ColumnDefinition<TRow>[] = [
    {
      id: 'record_name',
      accessor: (row) => cellText(row.record_name) ?? '',
      header: t('tenant.modules.records.columns.name'),
      sortable: sortable.has('record_name'),
      hideable: false,
      headerFilter: { category: 'text', fieldSlug: 'record_name' },
      renderCell: (_v, row) => (
        <div>
          <div className="font-medium text-text-primary">{cellText(row.record_name) ?? '—'}</div>
          {row.is_archived === true && (
            <span className="text-xs text-text-muted">{t('tenant.modules.records.archived')}</span>
          )}
        </div>
      ),
    },
  ];

  if (hasNumbering) {
    cols.push({
      id: 'record_number',
      accessor: (row) => cellText(row.record_number) ?? '',
      header: t('tenant.modules.records.columns.number'),
      // `record_number` jest celowo POZA `meta.sortable` (backend, silnik list) — sprawdzamy
      // `sortableSlugs` zamiast zaszytego `false` dla spójności z resztą kolumn (gdyby kiedyś
      // backend to zmienił, front nie wymaga edycji).
      sortable: sortable.has('record_number'),
      headerFilter: { category: 'text', fieldSlug: 'record_number' },
      renderCell: (_v, row) => {
        const number = cellText(row.record_number);
        return number ? (
          <span className="font-mono text-xs font-medium text-text-secondary">{number}</span>
        ) : (
          <span className="text-xs text-text-muted">—</span>
        );
      },
    });
  }

  if (categories.length > 0) {
    cols.push({
      id: 'category',
      accessor: (row) => cellText(row.category_id) ?? '',
      header: t('tenant.modules.records.columns.category'),
      sortable: sortable.has('category'),
      // Filtr wiersza = lista kategorii (wartość = id kategorii, jak w zakładce „Wartości”
      // popupu); `kanbanFallback: false` — opcje służą tu tylko filtrowi, nie grupowaniu Kanbana.
      headerFilter: {
        category: 'select',
        fieldSlug: 'category',
        kanbanFallback: false,
        options: categories.map((category) => ({
          value: category.id,
          label: category.name,
          color: category.color,
        })),
      },
      renderCell: (_v, row) => {
        const rt = categories.find((r) => r.id === cellText(row.category_id));
        return rt ? (
          <span
            className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium"
            style={{ backgroundColor: `${rt.color}22`, color: rt.color }}
          >
            <span
              className="h-1.5 w-1.5 rounded-full shrink-0"
              style={{ backgroundColor: rt.color }}
            />
            {rt.name}
          </span>
        ) : (
          <span className="text-xs text-text-muted">—</span>
        );
      },
    });
  }

  for (const field of fields) {
    const filter = moduleFieldFilter(field, filterOptions, emptyOptionLabel);
    cols.push({
      // Identyfikator kolumny = slug (nie pole.id). Dla modułów własnych slug pełni rolę
      // logicznego klucza pola — spójnie z `list_config.columns`, Kreatorem Listy, kanbanConfig
      // (groupByFieldId/cardFields = slugi) oraz danymi rekordu (`row[slug]`). Listy systemowe
      // analogicznie używają semantycznych id kolumn (np. `status`).
      id: field.slug,
      accessor: (row) => row[field.slug] ?? '',
      header: field.name ?? field.slug,
      // Pole jednokolumnowe (slug == id kolumny) jest sortowalne wtedy i tylko wtedy, gdy backend
      // go zwrócił w `meta.sortable`. Złożone (address/daterange/location) nigdy tam nie
      // trafiają — zostają niesortowalne bez dodatkowej logiki po stronie frontu.
      sortable: sortable.has(field.slug),
      ...(filter.filterable ? {} : { filterable: false }),
      ...(filter.headerFilter ? { headerFilter: filter.headerFilter } : {}),
      renderCell: (_v, row) => FieldRegistry.renderCell(field, row[field.slug]),
    });
  }

  return cols;
}

/**
 * Dotychczasowa reguła kategorii filtra dla KOLUMN KANBANA (bez zmian względem listy sprzed
 * fali 5): pola złożone bez filtra, pola wyboru jako `select` (opcje = tory tablicy), reszta
 * jak dotąd. Kanban nie ma wiersza filtrów, więc nowe reguły listy (`moduleFieldFilter`) go
 * nie dotyczą.
 */
function kanbanFilterCategory(fieldType: string): ColumnHeaderFilterConfig['category'] | null {
  if (fieldType === 'address' || fieldType === 'daterange' || fieldType === 'location') return null;
  if (KANBAN_NUMERIC_FIELD_TYPES.has(fieldType)) return 'number';
  if (DATE_FIELD_TYPES.has(fieldType)) return 'date';
  if (['select', 'radio', 'status', 'relation', 'lookup'].includes(fieldType)) return 'select';
  if (BOOLEAN_FIELD_TYPES.has(fieldType)) return 'boolean';
  return 'text';
}

/** Argumenty `buildModuleKanbanColumns`. */
export interface BuildModuleKanbanColumnsArgs {
  t: ModuleListTranslateFn;
  /** WSZYSTKIE aktywne pola (pole grupujące bywa ukryte w tabeli). */
  fields: ReadonlyArray<ModuleField>;
}

/**
 * Kolumny ujednoliconej tablicy Kanban (id = slug, jak kolumny tabeli). Osobna lista zamiast
 * reużycia kolumn tabeli, bo: (1) Kanban potrzebuje WSZYSTKICH pól, nie tylko widocznych;
 * (2) polom wyboru dajemy `headerFilter.options` z definicji pola — stąd etykiety i kolejność
 * kolumn tablicy (torów). Pierwsza kolumna = tytuł kafelka (nazwa rekordu). Spójne
 * z `kanbanConfig.groupByFieldId`/`cardFields`.
 */
export function buildModuleKanbanColumns(
  args: BuildModuleKanbanColumnsArgs,
): ColumnDefinition<ModuleRecord>[] {
  const { t, fields } = args;
  const cols: ColumnDefinition<ModuleRecord>[] = [
    {
      id: 'record_name',
      accessor: (row) => row.record_name ?? '',
      header: t('tenant.modules.records.columns.name'),
      sortable: false,
      renderCell: (_v, row) => (
        <span className="font-medium text-text-primary">{row.record_name ?? '—'}</span>
      ),
    },
  ];
  for (const field of fields) {
    const category = kanbanFilterCategory(field.fieldType);
    let headerFilter: ColumnHeaderFilterConfig | undefined;
    if (category) {
      headerFilter = { category, fieldSlug: field.slug, fieldType: field.fieldType };
      const options = field.options;
      if (category === 'select' && Array.isArray(options) && options.length > 0) {
        headerFilter.options = options;
      }
    }
    cols.push({
      id: field.slug,
      accessor: (row) => (row[field.slug] as unknown) ?? '',
      header: field.name ?? field.slug,
      sortable: false,
      ...(headerFilter ? { headerFilter } : {}),
      renderCell: (_v, row) => FieldRegistry.renderCell(field, row[field.slug]),
    });
  }
  return cols;
}
